import { api } from "@/lib/api";

export const MAX_VIDEO_BYTES = 1024 * 1024 * 1024;

export function uploadErrorMessage(error) {
  const status = error.response?.status;
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (status === 413) return "The server rejected an upload part (413). Your saved parts are kept. The live site may need the latest upload update.";
  if (status === 401) return "Your login has expired. Log in again, then retry your upload.";
  if (status === 404) return "The upload service or session is unavailable (404). The site may need its latest update; try selecting your video again.";
  if (status === 422) return "Check your video and clip settings. Videos must be a supported format and no larger than 1 GB.";
  if (status === 507) return "Video storage is unavailable or full. Contact the site owner; your saved parts are retained.";
  if (!error.response) return "The connection was interrupted. Keep this page open and choose Retry upload to continue from saved parts.";
  if (status >= 500) return `The upload service is temporarily unavailable (${status}). Retry upload will continue from saved parts.`;
  return `Upload could not finish${status ? ` (${status})` : ""}. Keep the page open and retry.`;
}

async function retryRequest(send, onRetry) {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await send();
    } catch (error) {
      if (error.code === 'ERR_CANCELED') throw error;
      const status = error.response?.status;
      const transient = !status || [408, 500, 502, 503, 504].includes(status);
      if (!transient || attempt === 2) throw error;
      onRetry(attempt + 1);
      await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }
}

export async function uploadVideoInParts(file, settings, session, onProgress, options = {}) {
  const retry = (send, phase) => retryRequest(send, (attempt) => onProgress({ phase: `${phase} · reconnecting (${attempt}/2)…` }));
  onProgress({ phase: "Checking your upload and clip allowance…", savedBytes: 0, progress: 0 });
  const { data: upload } = await retry(() => api.post("/uploads", {
    ...settings, upload_id: session.id, filename: file.name, size: file.size,
  }, { timeout: 30000, ...(options.signal ? { signal: options.signal } : {}) }), "Preparing upload");
  const params = { trial_id: settings.trial_id };
  const received = new Set(upload.received_parts);
  let savedBytes = upload.received_bytes;
  onProgress({ savedBytes, progress: Math.floor(savedBytes / file.size * 100), phase: "Continuing from saved video parts…" });
  let nextPart = 0;
  let failure = null;
  const pending = Array.from({ length: upload.total_parts }, (_, index) => index).filter((index) => !received.has(index));
  const sendParts = async () => {
    while (!failure && nextPart < pending.length) {
      const index = pending[nextPart++];
      const part = file.slice(index * upload.chunk_size, Math.min(file.size, (index + 1) * upload.chunk_size));
      try {
        await retry(() => api.put(`/uploads/${session.id}/parts/${index}`, part, {
          params, headers: { "Content-Type": "application/octet-stream" }, timeout: 180000,
          ...(options.signal ? { signal: options.signal } : {}),
        }), `Sending part ${index + 1} of ${upload.total_parts}`);
        received.add(index);
        savedBytes += part.size;
        onProgress({ savedBytes, progress: Math.floor(savedBytes / file.size * 100), phase: `Saved ${received.size} of ${upload.total_parts} parts` });
      } catch (error) {
        failure = failure || error;
      }
    }
  };
  onProgress({ phase: `Sending video parts · ${received.size} of ${upload.total_parts} saved…` });
  // Keep 4 MB requests; wait for in-flight parts before offering a safe manual retry.
  await Promise.all(Array.from({ length: Math.min(3, pending.length) }, sendParts));
  if (failure) throw failure;
  if (options.deferProcessing) {
    onProgress({ phase: "All video parts saved · ready to attach", progress: 100, savedBytes });
    return { upload_id: session.id };
  }
  onProgress({ phase: "All parts saved · starting video processing…", progress: 100, savedBytes });
  const { data } = await retry(() => api.post(`/uploads/${session.id}/complete`, null, { params, timeout: 30000 }), "Starting processing");
  return data;
}
